import {
  useCallback,
  useEffect,
  useState,
  type FC,
  type PropsWithChildren
} from 'react';
import { useAuthInit } from 'utils/contexts/AuthProvider';
import ErrorBoundary from '.';

const buttonClasses =
  'inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold transition-colors duration-150 cursor-pointer';

/**
 * Styled with Tailwind utilities only: this screen renders at the app root,
 * outside the MUI theme boundary (which lives inside the dashboard layout),
 * and inside prerendered landing pages.
 */
export const DefaultErrorScreen: FC<{
  error?: {
    name?: string;
    message?: string;
  };
  onResetError?: () => void;
}> = ({ onResetError, error }) => {
  const { logout } = useAuthInit();

  /**
   * Refresh the browser if the user gets an error message to say they are offline,
   * but then goes back online.
   */
  useEffect(() => {
    const handleOnline = () => {
      window.location.reload();
    };
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const isChunkLoadError = error?.name === 'ChunkLoadError';

  useEffect(() => {
    if (isChunkLoadError) {
      window.location.reload();
    }
  }, [isChunkLoadError]);

  return (
    <div className="flex h-dvh w-dvw items-center justify-center overflow-hidden bg-[#f2f3f1] text-center font-sans">
      <div
        data-testid="content-container"
        className="mx-4 max-w-lg rounded-xl border border-black/10 bg-white p-8 shadow-xl"
      >
        <h1 className="mb-6 text-4xl font-bold text-[#ea580c]">Oops!</h1>
        <p data-testid="error-message" className="mb-4 text-base leading-relaxed text-[#64748b]">
          {error?.message}
        </p>
        <p className="mb-4 text-base leading-relaxed text-[#64748b]">
          This is not supposed to happen. Please try again later.
        </p>

        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <button
            type="button"
            className={`${buttonClasses} bg-[#ea580c] text-white hover:bg-[#c2410c]`}
            onClick={() => {
              onResetError?.();
              window.history.pushState({}, '', '/');
              window.location.reload();
            }}
          >
            Back Home
          </button>

          <button
            type="button"
            className={`${buttonClasses} border border-[#ea580c] text-[#ea580c] hover:bg-[#fff7ed]`}
            onClick={async () => logout()}
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

const UnhandledErrorBoundary: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuthInit();
  const [error, setError] = useState<{
    name: string;
    message: string;
  } | null>(null);

  const promiseRejectionHandler = useCallback((event: PromiseRejectionEvent) => {
    setError(event.reason);
  }, []);

  const handleResetError = useCallback(async () => {
    setError(null);
    window.history.pushState({}, '', window.location.pathname);
    if (auth.isAuthenticated) {
      await auth.logout();
    }
  }, [auth]);

  useEffect(() => {
    window.addEventListener('unhandledrejection', promiseRejectionHandler);

    return () => {
      window.removeEventListener('unhandledrejection', promiseRejectionHandler);
    };
  }, [promiseRejectionHandler]);

  return error ? (
    <DefaultErrorScreen error={error} onResetError={handleResetError} />
  ) : (
    <>{children}</>
  );
};

export const DefaultErrorBoundary: FC<PropsWithChildren> = ({ children }) => (
  <UnhandledErrorBoundary>
    <ErrorBoundary
      fallback={(error, handleResetError) => (
        <DefaultErrorScreen onResetError={handleResetError} error={error} />
      )}
    >
      {children}
    </ErrorBoundary>
  </UnhandledErrorBoundary>
);
